import { CommonModule } from '@angular/common';
import { Component, Input, OnInit, OnChanges, Output, EventEmitter, ViewChild, AfterViewInit } from '@angular/core';
import { trigger, state, style, transition, animate } from '@angular/animations';
import { FormsModule } from '@angular/forms';
import {
  RappiderChatModule,
  RappiderDropdownMenuModule
} from '@rappider/rappider-components';
import {
  AvatarComponentConfig,
  AvatarShape,
  IconType,
  MessageType,
} from '@rappider/rappider-components/utils';
import { CardIconTitleDescription } from '@rappider/rappider-components';
import { Action } from '@rappider/rappider-components/utils';
import { AiFunctionCallControllerService, ProjectChannelMessage } from '@rappider/rappider-sdk';
import { AngularSplitModule } from 'angular-split';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzCardModule } from 'ng-zorro-antd/card';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzInputModule } from 'ng-zorro-antd/input';
import { NzMessageServiceModule } from 'ng-zorro-antd/message';
import { NzRadioModule } from 'ng-zorro-antd/radio';
import { NzToolTipModule } from 'ng-zorro-antd/tooltip';
import { finalize, catchError, of } from 'rxjs';
import { v4 as uuid } from 'uuid';
import { ChatInputComponent } from '../chat-input/chat-input.component';
import { RappiderChatMessagesComponent } from '../chat-messages/chat-messages.component';
import { Store } from '@ngrx/store';
import { CollapseLeftSidebar } from 'apps/rappider/src/app/state/app.actions';
import {
  AIResponseData,
  SplitterEvent
} from '../../utils/ai-response-types.interface';
import { AiCanvasRendererComponent } from
  '../ai-canvas-components/ai-canvas-renderer.component';
import { RappiderAiStartComponent } from '../ai-start/ai-start.component';
import {
  AiChatConfig,
  AIGenerationType,
  getGenerationType
} from '../../utils/ai-response-types.interface';
import { AI_CHAT_CONFIGS } from '../../configs/ai-chat-config';

const aiSenderId = '00000000-0000-0000-0000-000000000001';

@Component({
  selector: 'rappider-ai-abstract-chat',
  standalone: true,
  imports: [
    CommonModule,
    NzInputModule,
    NzIconModule,
    NzButtonModule,
    FormsModule,
    NzRadioModule,
    NzToolTipModule,
    RappiderChatModule,
    NzButtonModule,
    NzMessageServiceModule,
    ChatInputComponent,
    RappiderChatMessagesComponent,
    AngularSplitModule,
    NzCardModule,
    RappiderDropdownMenuModule,
    AiCanvasRendererComponent,
    RappiderAiStartComponent
  ],
  templateUrl: './ai-abstract-chat.component.html',
  styleUrls: ['./ai-abstract-chat.component.scss'],
  animations: [
    trigger('slideInOut', [
      state('in', style({
        transform: 'translateY(0)',
        opacity: 1
      })),
      state('out', style({
        transform: 'translateY(-50px)',
        opacity: 0
      })),
      transition('in => out', animate('800ms cubic-bezier(0.25, 0.8, 0.25, 1)')),
      transition('out => in', animate('600ms cubic-bezier(0.25, 0.8, 0.25, 1)'))
    ]),
    trigger('fadeInUp', [
      state('in', style({
        transform: 'translateY(0)',
        opacity: 1
      })),
      state('out', style({
        transform: 'translateY(20px)',
        opacity: 0
      })),
      transition('out => in', animate('1000ms cubic-bezier(0.25, 0.8, 0.25, 1)')),
      transition('in => out', animate('500ms cubic-bezier(0.25, 0.8, 0.25, 1)'))
    ])
  ]
})
export class AiAbstractChatComponent implements OnInit, OnChanges, AfterViewInit {
  /**
   * The unique identifier for the AI function to be executed.
   * This is a required input for the component to operate.
   * @type {string}
   */
  @Input() aiFunctionId?: string;
  @Input() activeProjectId: string | undefined;
  @Input() activePersonId: string | undefined;
  @Input() aiStartConfig?: AiChatConfig;

  @Output() messageSent = new EventEmitter<{ message: string; files?: File[] }>();
  @Output() responseAction = new EventEmitter<{ type: string; data: unknown }>();
  @Output() revertRequested = new EventEmitter<{ messageId: string; draftId: string; stepId: string }>();

  @Input() selectedProjectModelId: string | undefined;
  @Input() componentDefinitionId: string | undefined;

  /* Actions items to show over the chat input */
  @Input() actionCardItems?: CardIconTitleDescription[] = [
    // {
    //   id: 'sample-action-card-id',
    //   title: 'Sample Action Card',
    //   key: 'sample-action-card',
    //   iconName: 'fa fa-solid fa-calendar-check',
    //   description: 'Sample action card description',
    // }
  ];

  @Input() actionButtons?: Action[] = [
    // {
    //   id: 'sample-action-id',
    //   text: 'Sample Action Button',
    //   icon: { name: 'fa fa-solid fa-calendar-check' },
    //   redirectUrl: '/some-path',
    //   tooltipText: 'Sample Action Button Tooltip',
    // }
  ];

  /* Splitter Configuration Inputs */
  @Input() splitterLeftContainerMinSize = 0;
  @Input() splitterRightContainerSize = 0;
  @Input() initialSplitterOpen = false;
  @Input() editingModelName?: string;
  @Input() showExamplePrompts = false;
  @Input() examplePrompts?: string[];
  @Input() projectModels?: any[];

  @Input() maxRightSplitterSize = 800;
  @Input() toggleSplitterAnimationDuration = 250;
  @Input() readableChatSize = 800;

  private toggleSplitterAnimationInterval: NodeJS.Timeout | null = null;

  public messages: ProjectChannelMessage[] = [];
  public isTyping = false;
  public aiCallInProgress = false;
  public showAiStart = true;

  @ViewChild('chatMessagesComponent', { static: false }) chatMessagesComponent!: RappiderChatMessagesComponent;

  receiverAvatar: AvatarComponentConfig = {
    shape: AvatarShape.Square,
    imageUrl: '/assets/img/logo/rappider-logo.png',
  };
  /* Configuration for the user's avatar in the chat interface. */
  senderAvatar: AvatarComponentConfig = {
    shape: AvatarShape.Square,
    icon: {
      name: 'fa-regular fa-user',
      type: IconType.FontAwesome,
    },
    colorSettings: {
      backgroundColor: 'var(--section-background-color)',
    },
  };

  /**
   * Add AI response to chat interface
   * @param messageData - AI message data to add to chat
   */
  public addAIResponse(messageData: unknown): void {
    let parsedData: AIResponseData;

    if (typeof messageData === 'string') {
      try {
        parsedData = JSON.parse(messageData);
      } catch (e) {
        return;
      }
    } else {
      parsedData = messageData as AIResponseData;
    }

    const aiMessage: ProjectChannelMessage = {
      id: parsedData.id || uuid(),
      message: parsedData.message || 'AI Response',
      type: MessageType.AI,
      date: parsedData.date || new Date().toISOString(),
      channelId: parsedData.channelId || (this.aiFunctionId ?? 'unknown-channel'),
      projectId: parsedData.projectId || (this.activeProjectId ?? 'unknown-project'),
      projectModelId: parsedData.projectModelId || this.selectedProjectModelId,
      senderId: parsedData.senderId || aiSenderId,
      status: parsedData.status || 'new',
      responseData: parsedData.responseData,
      responseType: parsedData.responseType,
      isEmptyResponse: parsedData.isEmptyResponse
    };

    this.messages.push(aiMessage);
    this.isTyping = false;
    this.aiCallInProgress = false;

    // Hide AI start when AI response is added
    if (this.showAiStart) {
      this.showAiStart = false;
      // Trigger splitter resize after animation completes
      setTimeout(() => {
        this.triggerSplitterResize();
      }, 1000); // Wait for animation to complete
    }
  }
  // #endregion Generic AI Response Handling

  constructor(
    private store: Store<unknown>,
    private aiFunctionCallControllerService: AiFunctionCallControllerService
  ) {
    // Constructor intentionally left empty
  }

  ngOnInit(): void {
    this.initializeComponent();
    this.initializeAiStartConfig();
    this.initializeSplitterState();
  }

  ngOnChanges(): void {
    this.initializeAiStartConfig();
  }

  // eslint-disable-next-line @angular-eslint/no-empty-lifecycle-method
  ngAfterViewInit(): void {
    // Component view initialization - intentionally left empty
  }

  /**
   * Initialize component with default settings
   */
  private initializeComponent(): void {
    // Component initialization logic
  }

  /**
   * Initialize splitter state based on input
   */
  private initializeSplitterState(): void {
    if (this.initialSplitterOpen && this.splitterRightContainerSize === 0) {
      this.splitterRightContainerSize = this.maxRightSplitterSize;
    }
  }

  /**
   * Initialize AI start configuration based on function ID
   */
  private initializeAiStartConfig(): void {
    if (!this.aiStartConfig) {
      const configType = this.getConfigTypeFromFunctionId();
      if (configType) {
        this.aiStartConfig = AI_CHAT_CONFIGS[configType];
      }
    }
  }

  /**
   * Get configuration type from AI function ID
   * @returns AI generation type or null if not found
   */
  private getConfigTypeFromFunctionId(): AIGenerationType | null {
    return this.aiFunctionId ? getGenerationType(this.aiFunctionId) : null;
  }

  /**
   * Get response type from AI function ID
   * @returns AI generation type for response handling
   */
  private getResponseTypeFromFunctionId(): AIGenerationType {
    return this.aiFunctionId ? getGenerationType(this.aiFunctionId) || AIGenerationType.DATA_MODEL : AIGenerationType.DATA_MODEL;
  }

  /**
   * Send message to Rappider AI system
   * @param message - Message text to send
   * @param files - Optional files to attach
   */
  public sendMessageToRapider(message: string, files?: File[]) {
    if (!message?.trim() || this.aiCallInProgress) {
      return;
    }

    this._addUserMessageToChat(message, files);
    this.messageSent.emit({ message, files });
  }

  /**
   * Handle AI start button click event
   * @param event - Event containing message, files, and config type
   */
  public onAiStartClicked(event: {
    message: string;
    files?: File[];
    configType?: string;
  }) {
    // Allow if there's a message OR files, and not in progress
    const hasMessage = event.message?.trim();
    const hasFiles = event.files && event.files.length > 0;

    if ((!hasMessage && !hasFiles) || this.aiCallInProgress) {
      return;
    }
    this._addUserMessageToChat(event.message, event.files);
    this.messageSent.emit({ message: event.message, files: event.files });
  }


  /**
   * Add user message to chat interface
   * @param message - User message text
   * @param files - Optional attached files
   */
  private _addUserMessageToChat(message: string, files?: File[]): void {
    const userMessage: ProjectChannelMessage = {
      id: uuid(),
      message: message,
      type: MessageType.User,
      date: new Date().toISOString(),
      channelId: this.aiFunctionId ?? 'unknown-channel',
      projectId: this.activeProjectId ?? 'unknown-project',
      senderId: this.activePersonId ?? 'unknown-user',
      status: 'new',
      attachedFiles: files
    };
    this.messages.push(userMessage);

    if (this.showAiStart) {
      this.showAiStart = false;
      setTimeout(() => {
        this.triggerSplitterResize();
      }, 1000);
    }

    setTimeout(() => {
      this.scrollToBottomSmooth();
    }, 50);
  }

  // /**
  //  * Fetch AI response for the given message
  //  * @param message - Message to send to AI
  //  */
  // private _fetchAiResponse(message: string): void {

  //   if (!this.aiFunctionId) {
  //     return;
  //   }

  //   this.isTyping = true;
  //   this.aiCallInProgress = true;

  //   const params = {
  //     id: this.aiFunctionId ?? 'unknown-function',
  //     body: {
  //       functionPrompt: message,
  //     },
  //   };

  //   this.aiFunctionCallControllerService
  //     .executeFunctionToolsCall$Json(params)
  //     .pipe(
  //       catchError((error) => {
  //         const apiErrorMessage = error?.error?.message || error?.message || error?.error?.error?.message || 'Sorry, I encountered an error. Please try again.';
  //         const errorMessage: ProjectChannelMessage = {
  //           id: uuid(),
  //           message: apiErrorMessage,
  //           type: MessageType.Message,
  //           date: new Date().toISOString(),
  //           channelId: this.aiFunctionId ?? 'unknown-channel',
  //           projectId: this.activeProjectId ?? 'unknown-project',
  //           senderId: this.activePersonId ?? 'unknown-user',
  //           status: 'error',
  //         };
  //         return of(errorMessage);
  //       }),
  //       finalize(() => {
  //         this.isTyping = false;
  //         this.aiCallInProgress = false;
  //       })
  //     )
  //     .subscribe((response: unknown) => {
  //       const responseData = response as Record<string, unknown>;
  //       if (responseData.sender === aiSenderId) {
  //         this.messages.push(responseData as ProjectChannelMessage);
  //       } else {
  //         const resultContent = (responseData.result as Record<string, unknown>)?.content;
  //         const directContent = responseData.content;
  //         const aiMessage = (resultContent || directContent || 'AI response generated successfully.') as string;

  //         const aiResponseData: AIResponseData = {
  //           id: uuid(),
  //           message: aiMessage,
  //           date: new Date().toISOString(),
  //           channelId: this.aiFunctionId ?? 'unknown-channel',
  //           projectId: this.activeProjectId ?? 'unknown-project',
  //           senderId: aiSenderId,
  //           status: 'new',
  //           responseData: responseData.result || responseData,
  //           responseType: this.getResponseTypeFromFunctionId(),
  //           isEmptyResponse: false
  //         };
  //         this.addAIResponse(aiResponseData);
  //       }
  //     });
  // }


  /**
   * Handle splitter drag end event
   * @param event - Splitter event containing size information
   */
  onSplitDragEnds(event: SplitterEvent) {
    this.splitterRightContainerSize = event.sizes[1];
    this.collapseLeftSidebarIfChatAreaIsNotReadable();
  }

  /**
   * Collapse left sidebar if chat area becomes too narrow
   */
  collapseLeftSidebarIfChatAreaIsNotReadable() {
    const chatAreaWidth = window.innerWidth - this.splitterRightContainerSize;
    if (chatAreaWidth < this.readableChatSize) {
      this.store.dispatch(new CollapseLeftSidebar());
    }
  }

  /**
   * Handle response action from AI components
   * @param event - Event containing action type and data
   */
  public onResponseAction(event: { type: string; data: unknown }) {
    this.responseAction.emit(event);
  }

  /**
   * Handle revert request from chat messages
   * @param event - Event containing messageId, draftId, and stepId
   */
  public onRevertRequested(event: { messageId: string; draftId: string; stepId: string }): void {
    this.revertRequested.emit(event);
  }

  /**
   * Toggle splitter right container with animation
   */
  onToggleSplitterRightContainer() {
    if (this.toggleSplitterAnimationInterval) {
      clearInterval(this.toggleSplitterAnimationInterval);
      this.toggleSplitterAnimationInterval = null;
    }

    const startSize = this.splitterRightContainerSize ?? 0;
    const targetSize = this.splitterRightContainerSize > 0 ? 0 : this.maxRightSplitterSize;
    const sizeChange = targetSize - startSize;
    const startTime = Date.now();

    this.toggleSplitterAnimationInterval = setInterval(() => {
      const elapsedTime = Date.now() - startTime;
      const progress = Math.min(elapsedTime / this.toggleSplitterAnimationDuration, 1);
      const easedProgress = progress * (2 - progress);
      this.splitterRightContainerSize = startSize + sizeChange * easedProgress;
      if (progress === 1) {
        if (this.toggleSplitterAnimationInterval) {
          clearInterval(this.toggleSplitterAnimationInterval);
          this.toggleSplitterAnimationInterval = null;
        }
        this.splitterRightContainerSize = targetSize;
        this.collapseLeftSidebarIfChatAreaIsNotReadable();
      }
    }, 10);
  }

  /**
   * Handle splitter right container collapse
   */
  onCollapseSplitterRightContainer() {
    // Handle splitter collapse
  }


  /**
   * Smoothly scroll to bottom of chat messages
   */
  private scrollToBottomSmooth(): void {
    setTimeout(() => {
      if (this.chatMessagesComponent) {
        this.chatMessagesComponent.scrollToBottom(false);
      }
    }, 100);
  }

  /**
   * Scroll to bottom of chat messages
   */
  public scrollToBottom(): void {
    this.scrollToBottomSmooth();
  }

  /**
   * Handle splitter toggle request
   * @param shouldOpen - Whether the splitter should be opened
   */
  onSplitterToggleRequested(shouldOpen: boolean): void {
    if (shouldOpen && this.splitterRightContainerSize === 0) {
      this.onToggleSplitterRightContainer();
    }
  }

  /**
   * Trigger splitter resize event
   */
  private triggerSplitterResize(): void {
    setTimeout(() => {
      window.dispatchEvent(new Event('resize'));
    }, 100);
  }

  /**
   * Handle file upload button click event
   */
  onFileUploadButtonClicked(): void {
    // Handle file upload button click
  }

  /**
   * Clear loading state (isTyping and aiCallInProgress)
   */
  public clearLoadingState(): void {
    this.isTyping = false;
    this.aiCallInProgress = false;
  }

}
